/**
 * OrderTableRow Component
 * Renders individual rows in the order details table
 */

import React from 'react';
import type { OrderTableColumn, OrderTableAction } from '../../types/components';
import type { OrderSummary } from '../../utils/types';
import { getNestedValue } from '../../utils/orderTableHelpers';
import { allOrdersHaveRack } from '../../utils/productionLineHelpers';
import { getRackPolicyForColumn } from '../../utils/rackPolicy';
import { OrderTableCell } from './OrderTableCell';
import { OrderImagesCells } from './OrderImagesCells';
import { RackCell } from './RackCell';
import { TemporaryRackCell } from './TemporaryRackCell';
import { SealedCheckboxCell } from './SealedCheckboxCell';
import { GlassCutCheckboxCell } from './GlassCutCheckboxCell';
import { SpacerCutCheckboxCell } from './SpacerCutCheckboxCell';
import { DrawingDoneCheckboxCell } from './DrawingDoneCheckboxCell';
import { InlineDateEditor } from '../production-line/InlineDateEditor';
import { Button } from './Button';
import { Undo } from 'lucide-react';
import { PrintLabelButtonCell } from './PrintLabelButtonCell';

interface OrderTableRowProps {
  order: OrderSummary;
  columns: OrderTableColumn[];
  actions?: OrderTableAction[];
  onEdit?: (order: OrderSummary) => void;
  onDelete?: (order: OrderSummary) => void;
  rowClassName?: string;
  allOrders?: OrderSummary[];
  onTriggerCompleteDialog?: (jobId: string, jobNumber: string) => void;
  tableMode?: 'view' | 'edit' | 'custom';
  rackContext?: 'production_line' | 'orders_edit' | 'job_details' | 'order_modal';
}

export const OrderTableRow: React.FC<OrderTableRowProps> = React.memo(({
  order,
  columns,
  actions = [],
  onEdit,
  onDelete,
  rowClassName = '',
  allOrders = [],
  onTriggerCompleteDialog,
  tableMode = 'view',
  rackContext
}) => {
  // Default actions if none provided
  const defaultActions: OrderTableAction[] = [];
  
  if (onEdit) {
    defaultActions.push({
      label: 'Edit',
      variant: 'secondary',
      onClick: onEdit
    });
  }
  
  if (onDelete) {
    defaultActions.push({
      label: 'Delete',
      variant: 'danger',
      onClick: onDelete
    });
  }

  const allActions = actions.length > 0 ? actions : defaultActions;
  const showActions = allActions.length > 0;

  // Apply row-level styling
  const rowClasses = [
    'hover:bg-gray-50',
    rowClassName
  ].filter(Boolean).join(' ');

  // Track image columns to render them as a group
  const imageColumns = ['images_available', 'image_types', 'image_actions'];
  let imageColumnsRendered = false;

  return (
    <tr className={rowClasses}>
      {columns.map((column, columnIndex) => {
        // For sticky columns, use explicit positioning
        const stickyStyle = column.sticky ? {
          position: 'sticky' as const,
          left: column.key === 'website_job_number' ? 0 : 
                column.key === 'delivery_postcode' ? 120 : 0,
          zIndex: 10,
          backgroundColor: 'rgb(249 250 251)',  // bg-gray-50 - light gray background
          minWidth: column.width,
          maxWidth: column.width,
          // Add shadow to delivery_postcode (last sticky column)
          ...(column.key === 'delivery_postcode' ? {
            boxShadow: '2px 0 5px -2px rgba(0, 0, 0, 0.15)'
          } : {})
        } : {};

        // Handle image columns specially - render all three at once
        if (imageColumns.includes(column.key)) {
          if (!imageColumnsRendered) {
            imageColumnsRendered = true;
            return (
              <OrderImagesCells
                key={`images-${order.id}`}
                orderId={String(order.id ?? '')}
                order={order}
              />
            );
          } else {
            // Skip subsequent image columns as they're rendered by OrderImagesCells
            return null;
          }
        }

        // Handle rack column with inline editing (policy-driven)
        if (column.key === 'rack') {
          const effectiveMode = tableMode === 'custom' ? 'view' : tableMode;
          const ctx = rackContext ?? 'job_details';
          const policy = getRackPolicyForColumn(order, 'rack', ctx, effectiveMode);
          if (!policy.useRack) {
            if (effectiveMode === 'edit') {
              return (
                <TemporaryRackCell
                  key={column.key}
                  order={order}
                  readOnly={false}
                />
              );
            }
            return (
              <td key={column.key} className="p-2 border text-center bg-gray-100" style={stickyStyle}>
                <span className="text-gray-400 text-sm italic">Use Temp Rack</span>
              </td>
            );
          }
          return (
            <RackCell
              key={column.key}
              order={order}
              readOnly={policy.readOnly}
            />
          );
        }

        // Handle potential delivery date readonly (sealing tab)
        if (column.key === 'potential_delivery_date_readonly') {
          const dateValue = (order as any).potential_delivery_date;
          return (
            <td key={column.key} className="p-2 border text-center" style={stickyStyle}>
              <span className="text-sm">{dateValue ? new Date(dateValue).toLocaleDateString() : '-'}</span>
            </td>
          );
        }

        // Handle production date columns with inline editing
        if (['glass_arrival_date', 'manufacture_date_editable', 'potential_delivery_date'].includes(column.key)) {
          // Only show editable dates on the first order from each job to avoid duplicates
          const isReadOnly = !(order as any).is_first_order_from_job;
          
          // Map column key to actual field name for manufacture date
          const fieldKey = column.key === 'manufacture_date_editable' ? 'manufacture_date' : column.key;
          
          
          return (
            <td key={column.key} className="p-2 border" style={stickyStyle}>
              <InlineDateEditor
                jobId={(order as any).job_id}
                field={fieldKey as keyof import('../../utils/types').JobDatesData}
                value={getNestedValue(order, fieldKey)}
                dates={{
                  glass_arrival_date: (order as any).glass_arrival_date,
                  manufacture_date: (order as any).manufacture_date,
                  potential_delivery_date: (order as any).potential_delivery_date
                }}
                jobStatus={(order as any).job_status}
                productionStatus={(order as any).job_production_status}
                lockData={(order as any).lock_data}
                isReadOnly={isReadOnly}
              />
            </td>
          );
        }

        // Handle sealing workflow columns
        if (column.key === 'sealed_checkbox') {
          return (
            <SealedCheckboxCell
              key={column.key}
              order={order}
            />
          );
        }

        if (column.key === 'print_label_workflow') {
          return (
            <PrintLabelButtonCell key={column.key} order={order} stickyStyle={stickyStyle} />
          );
        }

        if (column.key === 'rack_workflow') {
          const effectiveMode = tableMode === 'custom' ? 'view' : tableMode;
          const policy = getRackPolicyForColumn(order, 'rack_workflow', rackContext, effectiveMode);
          if (!policy.showEditor) {
            return (
              <td key={column.key} className="p-2 border text-center bg-gray-100" style={stickyStyle}>
                <span className="text-gray-400 text-sm italic">{policy.placeholder}</span>
              </td>
            );
          }
          return (
            <RackCell
              key={column.key}
              order={order}
              readOnly={policy.readOnly}
            />
          );
        }

        // Handle glass cutting workflow columns
        if (column.key === 'glass_cut_checkbox') {
          return (
            <GlassCutCheckboxCell
              key={column.key}
              order={order}
            />
          );
        }

        if (column.key === 'print_label_glass_cutting') {
          return <PrintLabelButtonCell key={column.key} order={order} />;
        }

        if (column.key === 'temporary_rack_glass_cutting') {
          const effectiveMode = tableMode === 'custom' ? 'view' : tableMode;
          const policy = getRackPolicyForColumn(order, 'temporary_rack_glass_cutting', rackContext, effectiveMode);
          if (!policy.showEditor) {
            return (
              <td key={column.key} className="p-2 border text-center bg-gray-100" style={stickyStyle}>
                <span className="text-gray-400 text-sm italic">{policy.placeholder}</span>
              </td>
            );
          }
          return (
            <TemporaryRackCell
              key={column.key}
              order={order}
              readOnly={policy.readOnly}
            />
          );
        }

        if (column.key === 'rack_glass_cutting') {
          const effectiveMode = tableMode === 'custom' ? 'view' : tableMode;
          const policy = getRackPolicyForColumn(order, 'rack_glass_cutting', rackContext, effectiveMode);
          if (!policy.showEditor) {
            return (
              <td key={column.key} className="p-2 border text-center bg-gray-100" style={stickyStyle}>
                <span className="text-gray-400 text-sm italic">{policy.placeholder}</span>
              </td>
            );
          }
          return (
            <RackCell
              key={column.key}
              order={order}
              readOnly={policy.readOnly}
            />
          );
        }

        // Handle spacer workflow columns
        if (column.key === 'spacer_cut_checkbox') {
          return (
            <SpacerCutCheckboxCell
              key={column.key}
              order={order}
            />
          );
        }

        if (column.key === 'print_label_spacer') {
          return (
            <PrintLabelButtonCell key={column.key} order={order} stickyStyle={stickyStyle} />
          );
        }


        // Handle drawing workflow columns
        if (column.key === 'drawing_done_checkbox') {
          return (
            <DrawingDoneCheckboxCell
              key={column.key}
              order={order}
            />
          );
        }

        if (column.key === 'print_label_drawing') {
          return (
            <PrintLabelButtonCell key={column.key} order={order} stickyStyle={stickyStyle} />
          );
        }

        if (column.key === 'temporary_rack_drawing') {
          const effectiveMode = tableMode === 'custom' ? 'view' : tableMode;
          const policy = getRackPolicyForColumn(order, 'temporary_rack_drawing', rackContext, effectiveMode);
          if (!policy.showEditor) {
            return (
              <td key={column.key} className="p-2 border text-center bg-gray-100" style={stickyStyle}>
                <span className="text-gray-400 text-sm italic">{policy.placeholder}</span>
              </td>
            );
          }
          return (
            <TemporaryRackCell
              key={column.key}
              order={order}
              readOnly={policy.readOnly}
            />
          );
        }

        if (column.key === 'rack_drawing') {
          const effectiveMode = tableMode === 'custom' ? 'view' : tableMode;
          const policy = getRackPolicyForColumn(order, 'rack_drawing', rackContext, effectiveMode);
          if (!policy.showEditor) {
            return (
              <td key={column.key} className="p-2 border text-center bg-gray-100" style={stickyStyle}>
                <span className="text-gray-400 text-sm italic">{policy.placeholder}</span>
              </td>
            );
          }
          return (
            <RackCell
              key={column.key}
              order={order}
              readOnly={policy.readOnly}
            />
          );
        }

        if (column.key === 'rack_spacer') {
          const effectiveMode = tableMode === 'custom' ? 'view' : tableMode;
          const policy = getRackPolicyForColumn(order, 'rack_spacer', rackContext, effectiveMode);
          return (
            <RackCell
              key={column.key}
              order={order}
              readOnly={policy.readOnly}
            />
          );
        }

        if (column.key === 'temporary_rack_dashboard') {
          const effectiveMode = tableMode === 'custom' ? 'view' : tableMode;
          const policy = getRackPolicyForColumn(order, 'temporary_rack_dashboard', rackContext, effectiveMode);
          if (!policy.showEditor) {
            return (
              <td key={column.key} className="p-2 border text-center bg-gray-100" style={stickyStyle}>
                <span className="text-gray-400 text-sm italic">{policy.placeholder}</span>
              </td>
            );
          }
          return (
            <TemporaryRackCell
              key={column.key}
              order={order}
              readOnly={policy.readOnly}
            />
          );
        }

        if (column.key === 'rack_dashboard') {
          const effectiveMode = tableMode === 'custom' ? 'view' : tableMode;
          const policy = getRackPolicyForColumn(order, 'rack_dashboard', rackContext, effectiveMode);
          if (!policy.showEditor) {
            return (
              <td key={column.key} className="p-2 border text-center bg-gray-100" style={stickyStyle}>
                <span className="text-gray-400 text-sm italic">{policy.placeholder}</span>
              </td>
            );
          }
          return (
            <RackCell
              key={column.key}
              order={order}
              readOnly={policy.readOnly}
            />
          );
        }

        // Handle complete button (dashboard only)
        if (column.key === 'complete_button') {
          // Condition 1: Only show on first order of each job
          const isFirstOrder = order.is_first_order_from_job;
          
          // Condition 2: All orders in job must have rack values
          const jobHasAllRacks = allOrdersHaveRack(allOrders, order.job_id);
          
          // Show button only if BOTH conditions are met
          const shouldShowButton = isFirstOrder && jobHasAllRacks;
          
          return (
            <td key={column.key} className="p-2 border text-center" style={stickyStyle}>
              {shouldShowButton ? (
                <button
                  className="inline-flex items-center px-3 py-1 border border-green-300 rounded-md text-sm bg-green-50 hover:bg-green-100 transition-colors text-green-700 font-semibold"
                  onClick={() => onTriggerCompleteDialog?.(order.job_id, order.website_job_number)}
                >
                  Complete
                </button>
              ) : (
                <span className="text-gray-400 text-sm">-</span>
              )}
            </td>
          );
        }

        // Handle D1 drawing fields column (drawing tab only)
        if (column.key === 'd1_drawing_fields') {
          const values = [];
          if (order.georgian_bar_type) values.push({ label: 'Georgian Bar', value: order.georgian_bar_type });
          if (order.decorative_leaded_pattern) values.push({ label: 'Decorative Lead', value: order.decorative_leaded_pattern });
          if (order.duplex_bar_type) values.push({ label: 'Duplex Bar', value: order.duplex_bar_type });
          
          return (
            <td key={column.key} className="p-2 border" style={stickyStyle}>
              {values.length > 0 ? (
                <div className="text-sm">
                  {values.map((item, i) => (
                    <div key={i} className="mb-1">
                      <span className="font-semibold">{item.label}:</span> {item.value}
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-gray-400">-</span>
              )}
            </td>
          );
        }

        // Handle D2 drawing fields column (drawing tab only)
        if (column.key === 'd2_drawing_fields') {
          const values = [];
          // Helper function to ensure "mm" suffix is added only once
          const formatThickness = (value: any) => {
            const strValue = String(value);
            return strValue.endsWith('mm') ? strValue : `${strValue}mm`;
          };
          
          if (order.georgian_bar_thickness) values.push({ label: 'Georgian Bar', value: formatThickness(order.georgian_bar_thickness) });
          if (order.decorative_lead_thickness_mm) values.push({ label: 'Decorative Lead', value: formatThickness(order.decorative_lead_thickness_mm) });
          if (order.duplex_bar_thickness) values.push({ label: 'Duplex Bar', value: formatThickness(order.duplex_bar_thickness) });
          
          return (
            <td key={column.key} className="p-2 border" style={stickyStyle}>
              {values.length > 0 ? (
                <div className="text-sm">
                  {values.map((item, i) => (
                    <div key={i} className="mb-1">
                      <span className="font-semibold">{item.label}:</span> {item.value}
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-gray-400">-</span>
              )}
            </td>
          );
        }

        // Handle D3 drawing fields column (drawing tab only)
        if (column.key === 'd3_drawing_fields') {
          const values = [];
          if (order.decorative_lead_color) values.push({ label: 'Decorative Lead', value: order.decorative_lead_color });
          
          return (
            <td key={column.key} className="p-2 border">
              {values.length > 0 ? (
                <div className="text-sm">
                  {values.map((item, i) => (
                    <div key={i} className="mb-1">
                      <span className="font-semibold">{item.label}:</span> {item.value}
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-gray-400">-</span>
              )}
            </td>
          );
        }

        // Handle send back buttons (dashboard only)
        if (column.key === 'send_back_sealing') {
          const isCompleted = order.sealing_completed === true;
          
          return (
            <td key={column.key} className="p-2 border text-center" style={stickyStyle}>
              {isCompleted ? (
                <button
                  className="inline-flex items-center px-2 py-1 border border-red-300 rounded-md text-xs bg-red-50 hover:bg-red-100 transition-colors text-red-700"
                  onClick={() => {
                    // This will be handled by parent component
                    if (typeof window !== 'undefined' && (window as any).handleSendBack) {
                      (window as any).handleSendBack(order.id, 'sealing');
                    }
                  }}
                >
                  <Undo className="w-3 h-3 mr-1" />
                  Send Back
                </button>
              ) : (
                <span className="text-gray-400 text-sm">Not Complete</span>
              )}
            </td>
          );
        }

        if (column.key === 'send_back_glass_cutting') {
          const isCompleted = order.glass_cutting_completed === true;
          
          return (
            <td key={column.key} className="p-2 border text-center" style={stickyStyle}>
              {isCompleted ? (
                <button
                  className="inline-flex items-center px-2 py-1 border border-red-300 rounded-md text-xs bg-red-50 hover:bg-red-100 transition-colors text-red-700"
                  onClick={() => {
                    // This will be handled by parent component
                    if (typeof window !== 'undefined' && (window as any).handleSendBack) {
                      (window as any).handleSendBack(order.id, 'glass_cutting');
                    }
                  }}
                >
                  <Undo className="w-3 h-3 mr-1" />
                  Send Back
                </button>
              ) : (
                <span className="text-gray-400 text-sm">Not Complete</span>
              )}
            </td>
          );
        }

        // Regular column rendering
        const value = getNestedValue(order, column.key);
        return (
          <OrderTableCell
            key={column.key}
            value={value}
            order={order}
            column={column}
            stickyStyle={stickyStyle}
          />
        );
      })}
      {showActions && (
        <td className="p-2 border">
          <div className="flex gap-2">
            {allActions
              .filter(action => !action.condition || action.condition(order))
              .map((action, index) => (
              <Button
                key={index}
                size="sm"
                variant={action.variant || 'secondary'}
                onClick={() => action.onClick(order)}
                disabled={action.loading?.(order) || false}
                icon={action.icon && React.createElement(action.icon, { className: 'w-4 h-4' })}
              >
                {action.label}
              </Button>
            ))}
          </div>
        </td>
      )}
    </tr>
  );
});

OrderTableRow.displayName = 'OrderTableRow';
